import { CaseDisplayGroup, CaseDisplayGroupLocationView, FormField, FormSection } from "generated/graphql";
import { stripNull } from "helpers/coll";

export type DisplayGroupLocationUnion =
  | { kind: "CaseInfo" }
  | { kind: "CaseHome" }
  | { kind: "Section"; sectionId: number }
  | { kind: "Field"; fieldId: number }
  | { kind: "Collect" }
  | { kind: "CasesFilter" }
  | { kind: "Undefined" };

function locationViewToUnion(view: CaseDisplayGroupLocationView): DisplayGroupLocationUnion {
  const sectionId = stripNull(view.sectionId);
  const fieldId = stripNull(view.fieldId);
  if (view.caseInfo) return { kind: "CaseInfo" };
  else if (view.caseHome) return { kind: "CaseHome" };
  else if (sectionId !== undefined) return { kind: "Section", sectionId: sectionId };
  else if (fieldId !== undefined) return { kind: "Field", fieldId: fieldId };
  else if (view.collect) return { kind: "Collect" };
  else if (view.casesFilter) return { kind: "CasesFilter" };
  else return { kind: "Undefined" };
}

function renderLocationName(
  location: DisplayGroupLocationUnion,
  fieldMap: Map<number, FormField>,
  sectionMap: Map<number, FormSection>
): string {
  switch (location?.kind) {
    case "CaseInfo":
      return "Case Info";
    case "CaseHome":
      return "Case Home";
    case "Section":
      return sectionMap.get(location.sectionId)?.name ?? "Undefined Section";
    case "Field": {
      // If found, include section prefix in the field name (e.g. 2.1 Audiogram Model B)
      const maybeField = fieldMap.get(location.fieldId);
      return `${maybeField?.outlinePrefix ?? ""} ${maybeField?.name ?? "Undefined Field"}`;
    }
    case "Collect":
      return "Collect";
    case "CasesFilter":
      return "Case Table Filter";
    case "Undefined":
      return "Unknown Location";
  }
}

export function useGroupLocationNames(
  caseDisplayGroups: CaseDisplayGroup[],
  fieldMap: Map<number, FormField>,
  sectionMap: Map<number, FormSection>
): Map<string, CaseDisplayGroup[]> {
  const groupsByLocationName = new Map<string, CaseDisplayGroup[]>();

  for (const displayGroup of caseDisplayGroups) {
    for (const location of displayGroup.locations) {
      const locationName = renderLocationName(locationViewToUnion(location), fieldMap, sectionMap);
      const groups = groupsByLocationName.get(locationName) || [];
      groups.push(displayGroup);
      groupsByLocationName.set(locationName, groups);
    }
  }

  return groupsByLocationName;
}
